import {
	Box,
	Icon,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemSecondaryAction,
	ListItemText,
	TextField
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { AddButton } from "~/components/Buttons";
import { BodyTextSmall } from "~/components/Typographies";
import {
	canAddSmtpAddress,
	MAX_SMTP_ADDRESSES,
	normalizeSmtpAddressEmail,
	type SmtpAddressInput
} from "~admin/email/helpers/smtpAddressListEditor";
import { smtpAddressLabel } from "~admin/email/helpers/smtpConnectionPresentation";

interface SmtpAddressListEditorProps {
	addresses: SmtpAddressInput[];
	emptyListError?: string;
	onChange: (addresses: SmtpAddressInput[]) => void;
}

const SmtpAddressListEditor = ({ addresses, emptyListError, onChange }: SmtpAddressListEditorProps) => {
	const { t } = useTranslation("common");
	const [email, setEmail] = useState("");
	const [displayName, setDisplayName] = useState("");

	const canAdd = canAddSmtpAddress(email, addresses);

	const addAddress = () => {
		if (!canAdd) return;
		onChange([...addresses, { displayName: displayName.trim(), email: normalizeSmtpAddressEmail(email) }]);
		setEmail("");
		setDisplayName("");
	};

	const removeAddress = (index: number) => {
		onChange(addresses.filter((_, i) => i !== index));
	};

	const handleEnter = (event: React.KeyboardEvent) => {
		if (event.key !== "Enter") return;
		event.preventDefault();
		addAddress();
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
			<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
				<TextField
					label="Address"
					onChange={(event) => setEmail(event.target.value)}
					onKeyDown={handleEnter}
					placeholder="orders@yourcompany.com"
					size="small"
					sx={{ flex: "1 1 220px" }}
					value={email}
				/>
				<TextField
					label="Display name (optional)"
					onChange={(event) => setDisplayName(event.target.value)}
					onKeyDown={handleEnter}
					placeholder="Your Company Orders"
					size="small"
					sx={{ flex: "1 1 220px" }}
					value={displayName}
				/>
				<AddButton disabled={!canAdd} onClick={addAddress} />
			</Box>

			{addresses.length >= MAX_SMTP_ADDRESSES && (
				<BodyTextSmall color="text.secondary" role="status">
					{t("admin.email.smtp.addressLimitReached", { count: MAX_SMTP_ADDRESSES })}
				</BodyTextSmall>
			)}

			{addresses.length > 0 ? (
				<List dense>
					{addresses.map((address, index) => (
						<ListItem key={address.email}>
							<ListItemIcon sx={{ minWidth: 36 }}>
								<Icon>email</Icon>
							</ListItemIcon>
							<ListItemText primary={smtpAddressLabel(address)} />
							<ListItemSecondaryAction>
								<IconButton aria-label="Remove address" edge="end" onClick={() => removeAddress(index)} size="small">
									<Icon>close</Icon>
								</IconButton>
							</ListItemSecondaryAction>
						</ListItem>
					))}
				</List>
			) : (
				<BodyTextSmall color={emptyListError ? "error" : "text.secondary"}>
					{emptyListError ?? "No addresses added yet"}
				</BodyTextSmall>
			)}
		</Box>
	);
};

export default SmtpAddressListEditor;
